import { css } from 'styled-components';
import { HBCheckboxCustomTheme } from './HBCheckbox.types';
import { vars } from '../../theme';

/*
    Utility functions
    HBCheckbox & HBRadioButton components.
*/

/* ------------------------------ Constants ------------------------------ */

export const CONTROL_COLORS = {
    unchecked: {
        default: vars.colors.text.tertiary,
        hover: vars.colors.text.primary,
        disabled: vars.colors.stroke.disabled
    },
    checked: {
        default: vars.colors.text.primary,
        hover: vars.colors.text.secondary,
        disabled: vars.colors.surface.disabled
    },
    indeterminate: {
        default: vars.colors.text.primary,
        hover: vars.colors.text.secondary,
        disabled: vars.colors.surface.disabled
    },
    error: {
        surface: vars.colors.surface.danger,
        border: vars.colors.stroke.danger,
        icon: vars.colors.base.danger
    }
} as const;

/* ------------------------------ Utilities ------------------------------ */
const errorStyle = css`
    background-color: ${CONTROL_COLORS.error.surface};
    border-color: ${CONTROL_COLORS.error.border};
`;

const uncheckedStyle = css`
    background-color: ${vars.colors.surface.item};
    border-color: ${CONTROL_COLORS.unchecked.default};
`;

interface IControlStateStyles {
    checked: boolean;
    disabled: boolean;
    error: boolean;
    isIndeterminate?: boolean;
    noFocusHover?: boolean;
    customTheme?: HBCheckboxCustomTheme;
}

export function getControlStateStyles({
    checked,
    disabled,
    error,
    isIndeterminate = false,
    noFocusHover = false,
    customTheme = null
}: IControlStateStyles) {
    if (customTheme) {
        if (error && !disabled) {
            return errorStyle;
        }

        if (checked) {
            return css`
                background-color: ${customTheme.background};
                border-color: ${customTheme.background};
            `;
        }

        return uncheckedStyle;
    }

    if (disabled) {
        if (checked) {
            return css`
                background-color: ${CONTROL_COLORS.checked.disabled};
                border-color: ${CONTROL_COLORS.checked.disabled};
            `;
        }

        if (isIndeterminate) {
            return css`
                background-color: ${CONTROL_COLORS.indeterminate.disabled};
                border-color: ${CONTROL_COLORS.indeterminate.disabled};
            `;
        }

        return css`
            background-color: ${vars.colors.surface.item};
            border-color: ${vars.colors.surface.disabled};
        `;
    }

    if (error) {
        return errorStyle;
    }

    if (isIndeterminate) {
        return css`
            background-color: ${CONTROL_COLORS.indeterminate.default};
            border-color: ${CONTROL_COLORS.indeterminate.default};

            ${!noFocusHover &&
            css`
                &:hover {
                    background-color: ${CONTROL_COLORS.checked.hover};
                    border-color: ${CONTROL_COLORS.checked.hover};
                }
            `}
        `;
    }

    if (checked) {
        return css`
            background-color: ${CONTROL_COLORS.checked.default};
            border-color: ${CONTROL_COLORS.checked.default};

            ${!noFocusHover &&
            css`
                &:hover {
                    background-color: ${CONTROL_COLORS.checked.hover};
                    border-color: ${CONTROL_COLORS.checked.hover};
                }
            `}
        `;
    }

    return css`
        ${uncheckedStyle}
        ${!noFocusHover &&
        css`
            &:hover {
                border-color: ${CONTROL_COLORS.unchecked.hover};
            }
        `}
    `;
}
